import { CardKind, CommandResult, Commands, canEngagePet } from "@highmarch/domain";
import { rareIndexOf } from "../rare/rareApi";
import type { Cell } from "@highmarch/domain";
import { PlacementView } from "../render/views";
import { reducedVfx } from "../ui/settings";
import { coveredRoadTiles } from "./coverage";
import { HINT_MS, LevelParts } from "./levelParts";
import { placeCommand } from "./placeCommand";
import { UpgradeAim } from "./upgradeAim";
import { upgradeRefusal } from "../ui/upgradePreview";
import { groundAt } from "../render/terrain";
import { ScoutStrip } from "../ui/supplies/scoutStrip";

/**
 * The card in hand on the map: the road option shown, the chosen tower spot, what a tap, a long-press, PLACE and
 * ROTATE do with them, and the placement marks that show it. Taps are judged where things are drawn (ScreenPicker).
 */
export class LevelTaps {
  /** Two taps this close in time (ms) and space (CSS px) are a double-tap. */
  static readonly DoubleTapMs = 350;
  static readonly DoubleTapPx = 40;
  /** The road option shown for the picked road card. */
  ghostIndex = 0;
  /** The chosen tower spot (tower card in hand), or null until the player taps one. */
  towerSpot: Cell | null = null;
  /** The tower the Move card in hand will move (M55), or −1 until the player taps one; its new square is `towerSpot`. */
  moveFrom = -1;
  /** Legal tower cells for the tower card in hand (−1 when none is), for the tray's "nowhere to build" state. */
  private towerCellCount = -1;
  private coverageFor: Cell | null = null;
  private coverage = -1;
  private lastTapX = NaN;
  private lastTapY = NaN;
  private lastTapAt = 0;
  /** MP-06: the Scout's Map's look-ahead in the tray (made on the first hand of a run that carries one). */
  private scout: ScoutStrip | null = null;

  constructor(private readonly p: LevelParts) {}

  /** The tray's pick: a card in hand, its first road option shown. */
  pick(i: number): void {
    this.ghostIndex = 0;
    // A road card with no fit at the open end is refused by the sim; say so, or the card just drops back silently.
    if (this.p.execute(Commands.pickCard(i)) === CommandResult.InvalidPlacement) this.p.hand.nudge("No room for that road — pick another", HINT_MS);
  }

  onTap(x: number, y: number): void {
    if (import.meta.env.DEV) console.debug(`[level] tap ${Math.round(x)},${Math.round(y)}`);
    const p = this.p, sim = p.sim, offer = sim.currentOffer;
    // Pets v2: the wild pup on the grass — a tap on it (where it is drawn, a thumb's reach round it) gives chase
    if ((offer === null || !offer.hasPick) && canEngagePet(sim) && p.scene.wild.hit(x, y, (wx, wy, wz) => p.rig.iso.worldToViewport(wx, wy, wz))) {
      if (p.execute(Commands.engagePet()) === CommandResult.Ok) p.afterSimChange(true);
      return;
    }
    if (offer === null || !offer.hasPick) {
      // No card in hand: tapping a tower (anywhere it is drawn, roof included) opens its sheet; tapping elsewhere closes
      // it; a double-tap on open ground shows the whole map, and a double-tap there zooms back in on that spot (C3).
      const tower = p.picker.towerAt(x, y);
      const now = performance.now();
      const enemy = tower || p.towerInfo.isOpen ? null : p.picker.enemyAt(x, y);
      if (tower) { p.towerInfo.open(tower); p.raise("towerSheetOpened"); p.afterSimChange(true); }
      else if (p.towerInfo.isOpen) p.towerInfo.close();
      else if (enemy) { this.showEnemyInfo(enemy.id); return; }
      else if (now - this.lastTapAt < LevelTaps.DoubleTapMs && Math.hypot(x - this.lastTapX, y - this.lastTapY) < LevelTaps.DoubleTapPx) {
        this.lastTapAt = 0;
        const at = p.rig.iso.wholeMap ? p.picker.groundUnder(x, y) : null;
        p.layout.toggleWholeMap(at);
        return;
      }
      this.lastTapX = x; this.lastTapY = y; this.lastTapAt = now;
      return;
    }
    // Tier 4: a special card in hand is chosen in the tray (the branch picker, LEARN), not on the map.
    if (offer.pickedCard.kind === CardKind.Ascend || offer.pickedCard.kind === CardKind.Skill) return;
    if (offer.pickedCard.kind === CardKind.Move) { this.moveTap(x, y); return; }
    if (offer.pickedCard.kind === CardKind.Tower) {
      // Two steps, like the road: a tap chooses the spot (snapping to the nearest legal cell, since a fingertip
      // covers more than one), and a second tap on it — or PLACE — builds. One tap used to build on whatever cell
      // was under the finger, so a tower could land a cell away from where the player meant.
      const spot = p.picker.nearestTowerCell(x, y);
      if (spot === null) { this.missHint("Tap a highlighted square by the road"); return; }
      if (this.towerSpot !== null && spot.equals(this.towerSpot)) { this.place(); return; }
      this.towerSpot = spot;
      p.raise("towerSpotChosen");
      p.afterSimChange(true);
      return;
    }
    if (offer.pickedCard.kind === CardKind.Path) {
      // Tapping the shown option places it; tapping another option's tile switches to it. Judged on screen, like the
      // tower spot: the option tile whose drawn centre is nearest the finger, within a thumb's reach.
      const hit = p.picker.nearestOnScreen(x, y, offer.ghosts.flatMap((g, i) => g.cells.map((c) => ({ c, i }))), (o) => o.c);
      if (hit === null) { this.missHint(offer.ghosts.length > 1 ? "Tap the glowing road, or a white route to switch" : "Tap the glowing road to lay it"); return; }
      const current = offer.ghosts[this.ghostIndex];
      if (hit.i === this.ghostIndex || current.cells.some((c) => c.equals(hit.c))) { p.execute(Commands.placeSegment(this.ghostIndex)); return; }
      this.ghostIndex = hit.i;
      p.afterSimChange(true);
      return;
    }
    if (offer.pickedCard.kind === CardKind.Upgrade) {
      // Choose, then confirm, like a tower spot: a tap chooses the tower drawn nearest the finger and the tray shows
      // what the card does to it; a second tap on it — or UPGRADE — spends the card. One tap used to spend it unseen.
      const tower = p.picker.towerAt(x, y);
      if (tower === null) { this.missHint("Tap a glowing tower or the fortress"); return; }
      const refusal = upgradeRefusal(sim, tower);   // max tier, or tier 3 waiting on an Ascend card (never silent)
      if (refusal !== null) { this.missHint(refusal); return; }
      if (tower.id === p.upgrade.target) { this.place(); return; }
      p.upgrade.target = tower.id;
      p.afterSimChange(true);
    }
  }

  /**
   * The Move card in hand (M55): a tap chooses the tower to move (any but the fortress), then a highlighted square for
   * it, and a second tap on that square — or MOVE — moves it. Once a tower is chosen a tap is read as a square first
   * (a square beside a tower is under its roof on screen); a tap with no square in reach that lands on another tower
   * switches to it, and one on the chosen tower lets it go.
   */
  private moveTap(x: number, y: number): void {
    const p = this.p, sim = p.sim;
    if (this.moveFrom >= 0) {
      const spot = p.picker.nearestTowerCell(x, y);
      if (spot !== null) {
        if (this.towerSpot !== null && spot.equals(this.towerSpot)) { this.place(); return; }
        this.towerSpot = spot;
        p.afterSimChange(true);
        return;
      }
    }
    const tower = p.picker.towerAt(x, y);
    if (tower === null) { this.missHint(this.moveFrom < 0 ? "Tap a glowing tower to move it" : "Tap a highlighted square by the road"); return; }
    if (sim.balance.towers[tower.archetype].isBase) { this.missHint("The fortress stays put — tap a tower"); return; }
    this.moveFrom = tower.id === this.moveFrom ? -1 : tower.id;
    this.towerSpot = null;
    p.afterSimChange(true);
  }

  /** Long-press: on an enemy (where it is drawn) shows its info (P1.2.8); on open ground plants the rally flag. */
  onLongPress(x: number, y: number): void {
    const p = this.p;
    // A card in hand owns the map (QoL C5): a finger held while choosing a spot must not plant the flag or open a tip.
    if (p.sim.currentOffer?.hasPick) return;
    const enemy = p.picker.enemyAt(x, y);
    if (enemy) { this.showEnemyInfo(enemy.id); return; }
    const cell = p.rig.pickCell(x, y, p.sim.grid);
    if (cell === null) return;
    if (p.sim.troops.walkable(cell)) p.rally.plant(cell);
  }

  /** The enemy card for a live enemy (QoL R1; it replaced the P1.2.8 long-press tooltip). */
  private showEnemyInfo(id: number): void {
    const e = this.p.sim.findEnemy(id);
    if (!e) return;
    this.p.enemyCard.show(this.p.sim.balance, { archetype: e.archetype, boss: e.isBoss, live: e, owned: this.p.session.ownedTowers, rare: rareIndexOf(this.p.sim, e) > 0 });
  }

  /** A tap that chose nothing: the tray's hint line says what would have. */
  private missHint(text: string): void { this.p.hand.nudge(text, HINT_MS); }

  /** PLACE: the shown road ghost, or the tower on its chosen spot. */
  place(): void {
    const command = placeCommand(this.p.sim.currentOffer, this.ghostIndex, this.towerSpot, this.p.upgrade.target, this.moveFrom);
    if (command === null) return;
    this.towerSpot = null;                                   // spent (or not a tower card): the next pick chooses again
    this.p.upgrade.target = -1;
    this.moveFrom = -1;
    this.p.execute(command);
  }

  rotateGhost(): void {
    const offer = this.p.sim.currentOffer;
    if (offer === null || !offer.hasPick || offer.ghosts.length === 0) return;
    this.ghostIndex = (this.ghostIndex + 1) % offer.ghosts.length;
    this.p.afterSimChange(true);
  }

  /** Road tiles the chosen tower spot would cover (QoL P5), or −1 with no tower card and spot in hand. */
  private spotCoverage(): number {
    const sim = this.p.sim, offer = sim.currentOffer;
    if (this.towerSpot === null || offer === null || !offer.hasPick || offer.pickedCard.kind !== CardKind.Tower) return -1;
    if (this.coverageFor !== this.towerSpot) {
      this.coverageFor = this.towerSpot;
      this.coverage = coveredRoadTiles(sim.road.roadCells.filter((c) => !c.equals(sim.grid.base)), this.towerSpot, sim.previewRange(offer.pickedCard.index).toNumber(), sim.balance.towers[offer.pickedCard.index].minRange.toNumber());
    }
    return this.coverage;
  }

  /** A new offer or pick: drops the choices that no longer apply and redraws the placement marks and the road preview. */
  showOffer(): void {
    const sim = this.p.sim, offer = sim.currentOffer, placement = this.p.scene.placement;
    const hasPick = offer !== null && offer.hasPick;
    const kind = offer !== null && hasPick ? offer.pickedCard.kind : null;
    if (kind !== CardKind.Move || sim.findTower(this.moveFrom) === null) this.moveFrom = -1;
    if (kind !== CardKind.Tower && (kind !== CardKind.Move || this.moveFrom < 0)) { this.towerSpot = null; this.towerCellCount = -1; }
    this.p.upgrade.settle();
    PlacementView.motion = !reducedVfx();
    const ghost = offer !== null && hasPick && offer.pickedCard.kind === CardKind.Path ? offer.ghosts[Math.min(this.ghostIndex, offer.ghosts.length - 1)] : undefined;
    this.p.scene.ground.setPreview(ghost ? [sim.road.openEnd, ...ghost.cells] : null);
    if (offer === null || !hasPick) { placement.clear(); this.ghostIndex = 0; }
    else if (offer.pickedCard.kind === CardKind.Path) placement.showGhost(offer.ghosts, Math.min(this.ghostIndex, offer.ghosts.length - 1), sim.road.openEnd, sim.grid);
    else if (offer.pickedCard.kind === CardKind.Upgrade) this.p.upgrade.showMarks();
    else if (offer.pickedCard.kind === CardKind.Ascend || offer.pickedCard.kind === CardKind.Skill) placement.clear();
    else if (offer.pickedCard.kind === CardKind.Move) this.showMoveMarks();
    else {
      const cells = sim.collectValidTowerCells();
      this.towerCellCount = cells.length;
      if (this.towerSpot !== null && !cells.some((c) => c.equals(this.towerSpot!))) this.towerSpot = null;
      const arch = offer.pickedCard.index;
      const existing = sim.towerSlots.filter((t) => t.alive).map((t) => ({ cell: t.cell, range: sim.rangeOf(t).toNumber() }));
      placement.showTowerSpot(cells, this.towerSpot, sim.balance.towers[arch].id, sim.previewRange(arch).toNumber(), existing, sim.balance.towers[arch].minRange.toNumber());
    }
  }

  /**
   * The Move card's marks (M55): before a tower is chosen, every tower it can move glows (the fortress dimmed); once
   * one is, the free squares it can go to, with its range drawn round the chosen one.
   */
  private showMoveMarks(): void {
    const sim = this.p.sim, placement = this.p.scene.placement, towers = this.p.scene.towers;
    const moving = sim.findTower(this.moveFrom);
    if (moving === null) {
      const alive = sim.towerSlots.filter((t) => t.alive), isBase = (t: { archetype: number }) => sim.balance.towers[t.archetype].isBase;
      const mark = (t: (typeof alive)[number]) => ({ cell: t.cell, top: towers.restBox(t.id)?.max.y ?? groundAt(t.cell.x, t.cell.y) + UpgradeAim.FallbackTowerHeight });
      placement.showUpgradeTargets(alive.filter((t) => !isBase(t)).map(mark), alive.filter(isBase).map((t) => t.cell), null, null, false);
      this.towerCellCount = -1;
      return;
    }
    const cells = sim.collectValidTowerCells();
    this.towerCellCount = cells.length;
    if (this.towerSpot !== null && !cells.some((c) => c.equals(this.towerSpot!))) this.towerSpot = null;
    const arch = sim.balance.towers[moving.archetype];
    const existing = sim.towerSlots.filter((t) => t.alive && t.id !== moving.id).map((t) => ({ cell: t.cell, range: sim.rangeOf(t).toNumber() }));
    placement.showTowerSpot(cells, this.towerSpot, arch.id, sim.rangeOf(moving).toNumber(), existing, arch.minRange.toNumber());
  }

  /** The tower the Move card in hand will move, for the tray: its name, or null until one is chosen. */
  private movingName(): string | null {
    const t = this.p.sim.findTower(this.moveFrom);
    return t === null ? null : this.p.sim.balance.towers[t.archetype].name;
  }

  /** The tray, for the card in hand as the map shows it. */
  refreshHand(undoable: boolean): void {
    this.p.hand.refresh(this.p.sim, this.ghostIndex, this.towerSpot !== null, this.towerCellCount, this.p.upgrade.inHand(), undoable, this.spotCoverage(), this.movingName());
    if ((this.p.sim.mods.supplies?.scout ?? 0) > 0) (this.scout ??= new ScoutStrip(this.p.hand.root)).refresh(this.p.sim);
  }

  /** The cell the placement camera frames besides the road: the chosen tower spot, or the tower an Upgrade aims at. */
  focusCell(): Cell | null {
    return this.towerSpot ?? this.p.sim.findTower(this.p.upgrade.target)?.cell ?? this.p.sim.findTower(this.moveFrom)?.cell ?? null;
  }
}
